import { useEffect, useRef } from 'react';

import type { PlanStarter, TodayRestHours } from '@heraion/shared';

import { useLoggerScreen } from './useLoggerScreen';
import { useLoggerCursor } from './useLoggerCursor';
import { usePinnedExercise } from './usePinnedExercise';
import { useFieldFocusSignal } from './useFieldFocus';
import { useRestAdvance } from './useRestAdvance';
import { useRestInvite } from './useRestInvite';

// The stack LoggerScreen owns ABOVE its Modal (LG-13): the screen bag, the
// cursor, the field-focus signal, the rest transition and the rest invite,
// merged into the context bag. Moved out of LoggerScreen for its size; the
// hooks run in the same order as before.
export function useLoggerScreenHost({
  onMinimize,
  onOpenPainMap,
  onOpenCreateHub,
  onBuildAnyway,
  onTodayRestHours,
  weeklyMinimumMet,
  onTodayEmptyChange,
  onNameChange,
  ...args
}: Parameters<typeof useLoggerScreen>[0] & {
  onMinimize?: (draftName: string | null) => void;
  onOpenPainMap?: (draftName: string | null) => void;
  onOpenCreateHub?: () => void;
  onBuildAnyway?: () => Promise<PlanStarter | null>;
  onTodayRestHours?: (hours: TodayRestHours) => void;
  weeklyMinimumMet?: boolean;
  onTodayEmptyChange?: (empty: boolean) => void;
  onNameChange?: (name: string | null) => void;
}) {
  // The full screen bag drives both the orchestration shell and the
  // timeline subtree. LoggerScreen destructures what it renders directly
  // (header / footer / modals); TimelineLoggerScreen reads
  // the same bag through LoggerContext.
  const base = useLoggerScreen(args);
  // Focus cursor lives HERE (above the Modal) so it survives minimize -> resume
  // (LG-13); merged into the context bag the timeline subtree reads. Keyed on
  // the session so a new workout resets it. See useLoggerCursor.
  const cursor = useLoggerCursor(base.draft.startedAt);
  // The post-log focus transition lives up here for the SAME reason the cursor
  // does (LG-13): it holds a decision that has been taken but not yet applied,
  // and minimize unmounts everything below the Modal. Held inside, a minimize
  // during a rest would destroy the pending transition while the timer (also
  // hoisted) kept counting, and the focus would simply never advance. It moves
  // the cursor, so it belongs beside the cursor.
  // Which field holds the keyboard, owned up here for the same LG-13 reason:
  // the rest-end resume that reads it runs in this body, and the fields that
  // report into it live below the Modal.
  const fieldFocus = useFieldFocusSignal();
  // #308: the Pin, read by the hand-over below, so it lives up here too.
  const pin = usePinnedExercise(base.draft.startedAt, base.draft.entries, {
    live: base.mode === 'log',
    restoredKeyRef: base.restoredKeyRef,
  });
  const restAdvance = useRestAdvance({
    restTimer: base.restTimer,
    cursor,
    anyFieldFocused: fieldFocus.anyFieldFocused,
    pin: { id: pin.pinnedId, entries: base.draft.entries },
  });
  // The longer-rest offer belongs to the running rest, so it lives beside it.
  const restInvite = useRestInvite({
    resting: restAdvance.resting,
    restTimer: base.restTimer,
  });
  // LG-75: the host minimizes on these two paths; it cannot see the draft, so
  // the current name rides along (null when blank, so the pill falls back).
  const trimmed = base.draft.name?.trim();
  const draftName = trimmed ? trimmed : null;
  // Live rename to the host. The callback rides a ref so an inline host
  // closure does not refire the effect; only a real name change does. The
  // mount value is not a rename: an empty draft must not wipe the opener's name.
  const onNameChangeRef = useRef(onNameChange);
  onNameChangeRef.current = onNameChange;
  const lastNameRef = useRef(draftName);
  useEffect(() => {
    if (lastNameRef.current === draftName) return;
    lastNameRef.current = draftName;
    onNameChangeRef.current?.(draftName);
  }, [draftName]);
  const minimizeWithName = onMinimize ? () => onMinimize(draftName) : undefined;
  const openPainMapWithName = onOpenPainMap
    ? () => onOpenPainMap(draftName)
    : undefined;
  const ctx = {
    ...base,
    ...cursor,
    pin,
    restAdvance,
    restInvite,
    fieldFocus,
    onOpenPainMap: openPainMapWithName,
    onOpenCreateHub,
    onTodayEmptyChange,
    onBuildAnyway,
    onTodayRestHours,
    weeklyMinimumMet,
  };
  return { ctx, base, cursor, restAdvance, restInvite, minimizeWithName };
}
